import { MIXPANEL_DEVICE_TYPE } from '@/app/utils/constants'
import { captureLazySentryException } from '@/app/providers/sentry/lazy'
import { OnrampProvider, DepositStateName } from '@/types'
import type { BridgeStateV2 } from '@injectivelabs/cctp-bridge'
import type {
  Deposit,
  QrDepositPollingError,
  OnrampProviderWithStatus
} from '@/types'

const COMPLETED_BRIDGE_STEP = 'COMPLETED' satisfies `${BridgeStateV2['step']}`

async function getOnboardingService() {
  const { onboardingService } = await import('@/app/services/onboarding')

  return onboardingService
}

export const useOnboardingStore = defineStore('onboarding', {
  state: () => ({
    isStartingBridge: false,
    isResumingBridge: false,
    isFetchingFiatUrl: false,
    safeAddress: '' as string,
    deposits: [] as Deposit[],
    pendingSessions: [] as BridgeStateV2[],
    qrDepositFailed: null as null | Deposit,
    qrDepositCompleted: null as null | Deposit,
    providers: [] as OnrampProviderWithStatus[],
    justCompleted: null as null | BridgeStateV2,
    justFailed: null as null | { error: string },
    activeBridgeState: null as null | BridgeStateV2,
    qrDepositPollingError: null as null | QrDepositPollingError
  }),

  getters: {
    hasPendingDeposits: (state) => {
      return state.deposits.some(
        (deposit) =>
          !deposit.failedAt && deposit.stateName !== DepositStateName.Completed
      )
    },

    hasPendingBridgeSessions: (state) => {
      return state.pendingSessions.length > 0
    },

    isBridgeActive: (state) => {
      return (
        state.activeBridgeState !== null &&
        state.activeBridgeState.step !== COMPLETED_BRIDGE_STEP
      )
    },

    requiresResume: (): boolean => {
      const onboardingStore = useOnboardingStore()

      return (
        onboardingStore.hasPendingBridgeSessions &&
        !onboardingStore.isBridgeActive
      )
    },

    isDepositInFlight: (): boolean => {
      const onboardingStore = useOnboardingStore()

      return (
        onboardingStore.isBridgeActive || onboardingStore.hasPendingDeposits
      )
    },

    hasActiveDeposit: (): boolean => {
      const onboardingStore = useOnboardingStore()

      return (
        onboardingStore.hasPendingDeposits ||
        onboardingStore.isBridgeActive ||
        onboardingStore.requiresResume
      )
    }
  },

  actions: {
    setSafeAddress(safeAddress: string) {
      this.safeAddress = safeAddress
    },

    setDeposits(deposits: Deposit[]) {
      this.deposits = deposits
    },

    setIsFetchingFiatUrl(isFetchingFiatUrl: boolean) {
      this.isFetchingFiatUrl = isFetchingFiatUrl
    },

    setJustFailed(justFailed: null | { error: string }) {
      this.justFailed = justFailed
    },

    setJustCompleted(justCompleted: null | BridgeStateV2) {
      this.justCompleted = justCompleted
    },

    setQrDepositCompleted(qrDepositCompleted: null | Deposit) {
      this.qrDepositCompleted = qrDepositCompleted
    },

    setQrDepositFailed(qrDepositFailed: null | Deposit) {
      this.qrDepositFailed = qrDepositFailed
    },

    setIsStartingBridge(isStartingBridge: boolean) {
      this.isStartingBridge = isStartingBridge
    },

    setIsResumingBridge(isResumingBridge: boolean) {
      this.isResumingBridge = isResumingBridge
    },

    setPendingSessions(pendingSessions: BridgeStateV2[]) {
      this.pendingSessions = pendingSessions
    },

    setActiveBridgeState(activeBridgeState: null | BridgeStateV2) {
      this.activeBridgeState = activeBridgeState
    },

    setQrDepositPollingError(error: null | QrDepositPollingError) {
      this.qrDepositPollingError = error
    },

    async fetchSafeAddress(params: {
      srcChainId: number
      dstChainId: number
      userAddress: string
      isFastTransfer: boolean
    }) {
      const onboardingStore = useOnboardingStore()
      const { dstChainId, isFastTransfer, srcChainId, userAddress } = params
      const onboardingService = await getOnboardingService()

      const response = await onboardingService.fetchSafeAddress({
        srcChainId,
        dstChainId,
        userAddress,
        isFastTransfer,
        device: MIXPANEL_DEVICE_TYPE
      })

      onboardingStore.setSafeAddress(response.safeAddress)

      return response
    },

    async fetchDeposits(address: string) {
      const onboardingStore = useOnboardingStore()
      const onboardingService = await getOnboardingService()
      const deposits = await onboardingService.fetchDeposits(address)

      onboardingStore.setDeposits(deposits.data)

      return deposits
    },

    clearJustCompleted() {
      useOnboardingStore().setJustCompleted(null)
    },

    clearJustFailed() {
      useOnboardingStore().setJustFailed(null)
    },

    clearQrDepositCompleted() {
      useOnboardingStore().setQrDepositCompleted(null)
    },

    clearQrDepositFailed() {
      useOnboardingStore().setQrDepositFailed(null)
    },

    clearQrDepositPollingError() {
      useOnboardingStore().setQrDepositPollingError(null)
    },

    async markDepositsNotified(depositIds: string[]) {
      if (depositIds.length === 0) {
        return
      }

      try {
        const onboardingService = await getOnboardingService()

        await onboardingService.markDepositsNotified(depositIds)
      } catch (error) {
        captureLazySentryException(error, {
          message: '[onboardingStore] markDepositsNotified error',
          attributes: {
            feature: 'onboarding',
            depositCount: depositIds.length,
            operation: 'mark-deposits-notified'
          }
        })
      }
    },

    async fetchFiatOnrampProviders() {
      const sharedGeoStore = useSharedGeoStore()
      const onboardingService = await getOnboardingService()

      const data = await onboardingService.fetchOnrampProviders(
        sharedGeoStore.country
      )

      this.providers = data.providersWithStatus
    },

    async fetchFiatDepositUrl(params: {
      amount: number
      provider: OnrampProvider
    }) {
      const authStore = useAuthStore()
      const sharedGeoStore = useSharedGeoStore()
      const onboardingStore = useOnboardingStore()
      const sharedWalletStore = useSharedWalletStore()
      const onboardingService = await getOnboardingService()

      onboardingStore.setIsFetchingFiatUrl(true)

      try {
        await authStore.refreshSession()

        const { safeAddress } = await onboardingService.fetchOnrampSafeAddress(
          sharedWalletStore.address
        )

        const providerRedirectPath: Record<OnrampProvider, string> = {
          [OnrampProvider.Moonpay]: '/onboarding/moonpay',
          [OnrampProvider.Coinbase]: '/onboarding/coinbase',
          [OnrampProvider.BinanceConnect]: '/onboarding/binance-connect'
        }

        const redirectPath = providerRedirectPath[params.provider]

        return await onboardingService.fetchOnrampQuoteUrl({
          amount: params.amount,
          provider: params.provider,
          walletAddress: safeAddress,
          country: sharedGeoStore.country,
          clientIp: sharedGeoStore.ipAddress,
          redirectUrl: `${window.location.origin}${redirectPath}`
        })
      } finally {
        onboardingStore.setIsFetchingFiatUrl(false)
      }
    },

    resetBridgeScopedState() {
      const onboardingStore = useOnboardingStore()

      onboardingStore.setJustFailed(null)
      onboardingStore.setJustCompleted(null)
      onboardingStore.setPendingSessions([])
      onboardingStore.setActiveBridgeState(null)
      onboardingStore.setIsStartingBridge(false)
      onboardingStore.setIsResumingBridge(false)
    },

    resetQrDepositScopedState() {
      const onboardingStore = useOnboardingStore()

      onboardingStore.setDeposits([])
      onboardingStore.setQrDepositFailed(null)
      onboardingStore.setQrDepositCompleted(null)
      onboardingStore.setQrDepositPollingError(null)
    },

    resetWalletScopedState() {
      const onboardingStore = useOnboardingStore()

      onboardingStore.resetBridgeScopedState()
      onboardingStore.resetQrDepositScopedState()
    }
  }
})
